/**
 * getFileGlyph: the ONE answer to "which icon does this file get?".
 *
 * Before this, three mappings answered it separately and disagreed: the card
 * (the since-deleted `AttachmentCard.getFileTypeIcon`), the source pill
 * (`SourcePill.describeSourceFromFile`) and the tree
 * (`entityIcons.getHierarchyNodeGlyph`), so one video was a movie, a videocam
 * or a paperclip depending on where you looked. The kind now comes from the
 * shared `classifyFile` (the same registry the Type filter and the backend use),
 * and the glyph for each kind lives in `FILE_GLYPH_REFS` below.
 *
 * Kinds with a Bower glyph use it. Kinds without one (video, instrument, code,
 * archive, and the page used by documents, presentations and other) use a
 * Material Symbols PLACEHOLDER: when Bower artwork lands, it replaces the entry
 * here and every surface picks it up.
 */

import { ARTIFACT_TYPE_CONFIG } from '@bower/shared/constants/artifact-types.generated';
import { FILE_KINDS, classifyFile } from '@bower/shared/constants/fileKinds';
import type { FileFacts, FileKindId, InstrumentTechniqueId } from '@bower/shared';
import { bowerGlyph, materialGlyph, type GlyphRef } from '../theme/glyph';

/** Glyph ids, as named by `FILE_KINDS[].glyph` in `@bower/shared`. */
export type FileGlyphId =
  | 'folder'
  | 'note'
  | 'protocol'
  | 'spreadsheet'
  | 'document'
  | 'presentation'
  | 'image'
  | 'video'
  | 'audio'
  | 'instrument'
  | 'code'
  | 'archive'
  | 'equipment'
  | 'link'
  | 'file';

export interface FileGlyphDescriptor {
  readonly glyphId: FileGlyphId;
  /** The file's format, e.g. 'PDF' or 'CZI'. Undefined when there is none to show. */
  readonly label?: string;
  readonly kind: FileKindId;
  readonly technique?: InstrumentTechniqueId;
}

/**
 * A folder, or a file's facts. A whole workspace `FileItem` (`kind: 'file'`)
 * is accepted as is: only `kind === 'folder'` means a folder.
 */
export type FileGlyphInput = { readonly kind: 'folder' } | (FileFacts & { readonly kind?: 'file' });

/** The page glyph: documents, presentations and other files carry their format on it. */
const PAGE = materialGlyph('draft');

export const FILE_GLYPH_REFS: Record<FileGlyphId, GlyphRef> = {
  folder: bowerGlyph('collection'),
  note: bowerGlyph('note'),
  protocol: bowerGlyph('protocol'),
  spreadsheet: bowerGlyph('spreadsheet'),
  image: bowerGlyph('photo'),
  audio: bowerGlyph('voice'),
  // Same source as ENTITY_ICONS.equipment, so the two cannot drift apart.
  equipment: materialGlyph(ARTIFACT_TYPE_CONFIG.equipment.icon),
  // Same source as ENTITY_ICONS.link, so the two cannot drift apart.
  link: materialGlyph(ARTIFACT_TYPE_CONFIG.link.icon),
  // Material placeholders until Bower artwork exists.
  video: materialGlyph('videocam'),
  instrument: materialGlyph('monitoring'),
  code: materialGlyph('code'),
  archive: materialGlyph('folder_zip'),
  document: PAGE,
  presentation: PAGE,
  file: PAGE,
};

/** Glyphs drawn as a page with the format written on it. */
export const PAGE_GLYPH_IDS: ReadonlySet<FileGlyphId> = new Set([
  'document',
  'presentation',
  'file',
]);

/** Kinds whose identity is the Bower type, not a file format: no label. */
const NATIVE_KINDS: ReadonlySet<FileKindId> = new Set(['notes', 'protocols', 'equipment', 'links']);

/**
 * A stored extension worth printing: short, no spaces, and at least one
 * letter. An all-digit tail ("4" from "Buffer pH 7.4") is a version or a
 * value, never a format; callers must also never derive an extension from a
 * TITLE (see CaptureRow), because "Gel image 3.2x" would
 * still yield "2x".
 */
const PLAUSIBLE_EXTENSION = /^(?=[a-z0-9.]*[a-z])[a-z0-9.]{1,12}$/;

const glyphIdOf = (kind: FileKindId): FileGlyphId =>
  (FILE_KINDS.find((k) => k.id === kind)?.glyph ?? 'file') as FileGlyphId;

export function getFileGlyph(input: FileGlyphInput): FileGlyphDescriptor {
  if (input.kind === 'folder') return { glyphId: 'folder', label: undefined, kind: 'folders' };

  const { kind, technique } = classifyFile(input);
  const ext = input.fileExtension?.trim().toLowerCase().replace(/^\./, '') ?? '';
  // A native Bower spreadsheet has no file format; an uploaded .xlsx does.
  const isNative = NATIVE_KINDS.has(kind) || input.artifactType === 'spreadsheet';
  const label = !isNative && PLAUSIBLE_EXTENSION.test(ext) ? ext.toUpperCase() : undefined;

  return {
    glyphId: glyphIdOf(kind),
    label,
    kind,
    ...(technique ? { technique } : {}),
  };
}

/** The glyph a descriptor draws, for surfaces that render a bare `GlyphRef` (the tree). */
export const fileGlyphRef = (descriptor: Pick<FileGlyphDescriptor, 'glyphId'>): GlyphRef =>
  FILE_GLYPH_REFS[descriptor.glyphId];
